import { MouseEvent } from 'react';
import { Badge, Button, Dropdown, Flex, Popconfirm, Space, Table, Typography } from 'antd';
import { withErrorHandling } from '@copilot/common/components/errorAndLoading/errorAndLoading';
import { ColumnsType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { blue, grey } from '@ant-design/colors';
import { ICampaignStatus, ICampaignType } from '@copilot/data/graphql/_generated';
import { UtilityFunctions } from '@copilot/common/utils/common';
import {
    CampaignStatsType,
    getCampaignStatsColor,
    getSimpleCampaignNameValidator,
} from '@copilot/common/utils/campaign';
import { isNil } from 'lodash';
import styles from './allCampaignsPage.module.less';
import { NavigateFunction, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import modalManager from '@copilot/common/utils/modalManager';
import LoadingIndicator from '@copilot/common/components/loadingIndicator/spinner';
import { CAMPAIGN_DETAILS_V2_MENU_ITEMS } from '@copilot/teams/src/pages/campaignDetails/components/campaignDetailsConstants';

export type AllCampaignsTableProps = {
    data: CampaignSummaryModel[];
    showTeamMembers?: boolean;
    canEditCampaigns: boolean;
    campaignNameValidator: ReturnType<typeof getSimpleCampaignNameValidator>;
    onDeleteCampaign: (campaignId: string) => void;
    isLoading: boolean;
    isError: boolean;
};

export type CampaignSummaryModel = {
    id: string;
    name: string;
    type: ICampaignType;
    status: ICampaignStatus;
    numActiveMembers?: number;
    stats: CampaignStats;
    members: {
        id: string;
        orgMemberId: string;
    }[];
    orgMember: {
        id: string;
        name: string;
    };
    launchedAt: number | undefined;
};

type CampaignStats = {
    numUserReplies: number;
    numUsersConnected: number | undefined;
    numInvitesSent: number | undefined;
    numSendMessages: number | undefined;
    percentConnected: number | undefined;
    percentReplied: number | undefined;
};

const CampaignColorWhitelist: ICampaignType[] = [ICampaignType.Prospecting];

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const TableWithErrorHandling = withErrorHandling(Table<CampaignSummaryModel>);

/**
 * Renderer for campaign percentage statistics
 * @param value
 * @param hasWarning
 * @returns
 */
const RateRenderer = (
    value: number | undefined,
    statsType: CampaignStatsType,
    campaignType: ICampaignType
) => {
    const isNA = value === undefined;

    return (
        <span
            style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: `${isNA || !CampaignColorWhitelist.includes(campaignType) ? 'inherit' : getCampaignStatsColor(value, statsType)}`,
                color: isNA ? 'rgba(0,0,0,0.25)' : 'inherit',
            }}
        >
            {isNA ? 'N/A' : UtilityFunctions.convertDecimalToPercentage(value / 100)}
        </span>
    );
};

/**
 * Create table's Column Definition
 * @param title column title
 * @param dataIndex property name or path to the data in CampaignSummaryModel
 * @param fnRender renderer for rendering the content in the table cell
 * @returns
 */
const getColumnDef = (
    title: string,
    dataIndex: string | string[],
    sortFn?: (a: CampaignSummaryModel, b: CampaignSummaryModel) => number,
    fnRender?: (value: any, record: CampaignSummaryModel, index: number) => React.ReactNode
) => {
    return {
        title,
        dataIndex,
        showSorterTooltip: false,
        sortDirections: sortFn ? (['ascend', 'descend'] as SortOrder[]) : undefined,
        sorter: sortFn,
        render: fnRender,
    };
};

/**
 * Renderer for whether campaign is active and provides action menu for campaigns
 * @param isActive
 * @param campaign
 * @param navigate
 * @param campaignNameValidator
 * @param onDeleteCampaign
 * @param canEditCampaigns
 */
const StatusRenderer = (
    isActive: boolean,
    campaign: CampaignSummaryModel,
    campaignNameValidator: ReturnType<typeof getSimpleCampaignNameValidator>,
    onDeleteCampaign: (campaignId: string) => void,
    canEditCampaigns: boolean
) => {
    if (!canEditCampaigns) {
        return (
            <Space>
                <Badge dot color={isActive ? blue[4] : grey[0]} />
                <span>{isActive ? 'Active' : 'Inactive'}</span>
            </Space>
        );
    } else {
        const menuItems = [
            {
                key: 'duplicate',
                label: <Typography.Text>Duplicate Campaign</Typography.Text>,
                onClick: () => {
                    modalManager.openCampaignCreationModal({
                        nameEditorValidator: campaignNameValidator,
                        campaignId: campaign.id,
                        defaultName: `${campaign.name} Copy`,
                    });
                },
            },
            {
                key: 'allocate',
                label: (
                    <Link
                        to={`/campaign/${campaign.id}#${CAMPAIGN_DETAILS_V2_MENU_ITEMS.SETTINGS}?openManage=true`}
                    >
                        <Typography.Text>
                            Allocate{' '}
                            {campaign.type === ICampaignType.Prospecting ? 'Invites' : 'Messages'}
                        </Typography.Text>
                    </Link>
                ),
            },
            {
                key: 'delete',
                label: (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Popconfirm
                            title="Are you sure you want to delete this campaign?"
                            placement="left"
                            onConfirm={() => onDeleteCampaign(campaign.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Typography.Text className={styles.campaignActionDeleteButton}>
                                Delete Campaign
                            </Typography.Text>
                        </Popconfirm>
                    </div>
                ),
            },
        ];
        return (
            <Flex gap={16} justify={'space-between'}>
                <Space>
                    <Badge dot color={isActive ? blue[4] : grey[0]} />
                    <span>{isActive ? 'Active' : 'Inactive'}</span>
                </Space>
                <Dropdown
                    menu={{
                        items: menuItems,
                        onClick: (e) => e.domEvent.stopPropagation(),
                    }}
                    trigger={['click']}
                >
                    <Button type="primary" onClick={(e) => e.stopPropagation()}>
                        Actions
                    </Button>
                </Dropdown>
            </Flex>
        );
    }
};
/**
 * Renderer for type of campaign
 * @param type
 */
const TypeRenderer = (type: string) => {
    return <span>{type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}</span>;
};

/**
 * Renderer for team member
 * @param name
 * @param campaign
 */
function MemberRenderer(name: string | undefined) {
    return (
        <Typography.Text className={`${styles.tableValue} ${isNil(name) ? styles.empty : ''}`}>
            {isNil(name) ? 'N/A' : name}
        </Typography.Text>
    );
}

function NumberRenderer(value: number | undefined) {
    return (
        <Typography.Text className={`${styles.tableValue} ${isNil(value) ? styles.empty : ''}`}>
            {isNil(value) ? 'N/A' : value}
        </Typography.Text>
    );
}

/**
 * Object describing mouse actions for rows in campaign table
 * @param campaign
 * @param navigate
 * @returns
 */
const RowMouseInteractions = (campaign: CampaignSummaryModel, navigate: NavigateFunction) => {
    const campaignUrlToOpen = `/campaign/${campaign.id}`;

    return {
        onClick: (event: MouseEvent) => {
            if (event.ctrlKey || event.metaKey) {
                window.open(campaignUrlToOpen, '_blank');
            } else {
                navigate(campaignUrlToOpen);
            }
        },
    };
};

/**
 * Campaign table
 * @param props
 * @returns
 */
const AllCampaignsTable: FCWithChildren<AllCampaignsTableProps> = (props) => {
    const {
        data,
        showTeamMembers,
        campaignNameValidator,
        onDeleteCampaign,
        canEditCampaigns,
        isLoading,
        isError,
    } = props;
    const navigate = useNavigate();

    const columns: ColumnsType<CampaignSummaryModel> = [
        getColumnDef('Campaign Name', 'name', (a, b) => a.name.localeCompare(b.name)),
        getColumnDef('Type', 'type', undefined, (value: string) => TypeRenderer(value)),
    ];
    if (showTeamMembers) {
        columns.unshift(
            getColumnDef(
                'Team Member',
                'orgMember',
                (a, b) => a.orgMember.name.localeCompare(b.orgMember.name),
                (value: { id: string; name: string }) => MemberRenderer(value.name)
            )
        );
    }
    columns.push(
        getColumnDef(
            'Invites Sent',
            ['stats', 'numInvitesSent'],
            (a, b) => (a.stats.numInvitesSent ?? 0) - (b.stats.numInvitesSent ?? 0),
            (value: number | undefined) => NumberRenderer(value)
        ),
        getColumnDef(
            'Connection Rate',
            ['stats', 'percentConnected'],
            (a, b) => (a.stats.percentConnected ?? 0) - (b.stats.percentConnected ?? 0),
            (value: number | undefined, record: CampaignSummaryModel) =>
                RateRenderer(value, CampaignStatsType.ConnectionRate, record.type)
        ),
        getColumnDef(
            'Reply Rate',
            ['stats', 'percentReplied'],
            (a, b) => (a.stats.percentReplied ?? 0) - (b.stats.percentReplied ?? 0),
            (value: number | undefined, record: CampaignSummaryModel) =>
                RateRenderer(value, CampaignStatsType.ReplyRate, record.type)
        ),
        getColumnDef('Status', 'status', undefined, (value: string, record) =>
            StatusRenderer(
                value === ICampaignStatus.Enabled,
                record,
                campaignNameValidator,
                onDeleteCampaign,
                canEditCampaigns
            )
        )
    );

    return (
        <TableWithErrorHandling
            dataSource={data}
            loading={{ spinning: isLoading, indicator: <LoadingIndicator isLoading /> }}
            error={isError}
            columns={columns}
            pagination={{
                showSizeChanger: true,
            }}
            rowClassName="clickable"
            rowKey={(campaign) => campaign.id}
            onRow={(campaign) => RowMouseInteractions(campaign, navigate)}
        />
    );
};

export default AllCampaignsTable;
